(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (root.document && !root.RealmWishlist) root.RealmWishlist = factory(root.document, root);
})(typeof window === 'undefined' ? globalThis : window, function (document, window) {
  'use strict';
  const storageKey = 'realm.wishlist.handles.v1', maximumItems = 60;
  const cleanHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value) ? value : null;
  function parseHandles(raw) {
    if (typeof raw !== 'string' || raw.length > 20000) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? Array.from(new Set(parsed.map(cleanHandle).filter(Boolean))).slice(0, maximumItems) : [];
    } catch (_) { return []; }
  }
  function createStore(storageAccess) {
    let handles = [], persistent = true;
    function read() {
      if (!persistent) return handles.slice();
      try {
        const storage = storageAccess();
        if (!storage) throw new Error('Storage unavailable');
        handles = parseHandles(storage.getItem(storageKey) || '[]');
        persistent = true;
      } catch (_) { persistent = false; }
      return handles.slice();
    }
    function write(next) {
      handles = next.slice(0, maximumItems);
      try {
        const storage = storageAccess(), value = JSON.stringify(handles);
        if (!storage) throw new Error('Storage unavailable');
        storage.setItem(storageKey, value);
        if (storage.getItem(storageKey) !== value) throw new Error('Storage did not retain the save');
        persistent = true;
      } catch (_) { persistent = false; }
      return { handles: handles.slice(), persistent, capacity: false };
    }
    function toggle(handle) {
      if (!cleanHandle(handle)) return { handles: handles.slice(), persistent, capacity: false, invalid: true };
      if (persistent) read();
      const index = handles.indexOf(handle);
      if (index !== -1) return write(handles.filter(item => item !== handle));
      if (handles.length >= maximumItems) return { handles: handles.slice(), persistent, capacity: true };
      return write([handle, ...handles]);
    }
    function remove(handle) {
      if (persistent) read();
      return write(handles.filter(item => item !== handle));
    }
    function synchronize(raw) {
      if (!persistent) return handles.slice();
      handles = parseHandles(raw || '[]');
      return handles.slice();
    }
    read();
    return { read, toggle, remove, synchronize, state: () => ({ handles: handles.slice(), persistent, capacity: false }) };
  }
  function productUrl(handle, rootUrl, location) {
    if (!cleanHandle(handle)) throw new Error('Invalid product handle');
    const base = new URL(rootUrl || '/', location.origin);
    if (base.origin !== location.origin || base.search || base.hash) throw new Error('Invalid local root');
    base.pathname = base.pathname.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle);
    const current = new URL(location.href);
    for (const key of ['preview_theme_id', 'preview_key']) if (current.searchParams.has(key)) base.searchParams.set(key, current.searchParams.get(key));
    return base;
  }
  function matchingCard(source, handle) {
    const cards = Array.from(source.querySelectorAll('.realm-product-card'));
    return cards.find(card => card.dataset.realmProductHandle === handle) || null;
  }
  async function fetchCard(handle, rootUrl, signal, environment) {
    const url = productUrl(handle, rootUrl, environment.location);
    url.searchParams.set('section_id', 'realm-wishlist-item');
    const controller = new AbortController(), abort = () => controller.abort();
    if (signal.aborted) controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const timer = environment.setTimeout(abort, 12000);
    try {
      const response = await environment.fetch(url.href, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
      if (controller.signal.aborted) throw new Error('Saved product request aborted');
      if (response.status === 404 || response.status === 410) return { unavailable: true };
      if (!response.ok) throw new Error('Saved product request failed');
      const html = await response.text();
      if (controller.signal.aborted) throw new Error('Saved product request aborted');
      const source = new environment.DOMParser().parseFromString(html, 'text/html'), card = matchingCard(source, handle);
      if (!card) throw new Error('Saved product data was not returned');
      return { card };
    } finally { environment.clearTimeout(timer); signal.removeEventListener('abort', abort); }
  }
  let cardSequence = 0;
  function prepareCard(card) {
    for (const script of card.querySelectorAll('script')) {
      if (script.getAttribute('type') !== 'application/json' || !script.hasAttribute('data-product-json')) script.remove();
    }
    const prefix = `RealmSavedCard-${++cardSequence}-`, identifiers = new Map();
    for (const node of card.querySelectorAll('[id]')) {
      const original = node.id;
      if (!original) continue;
      const next = prefix + original;
      identifiers.set(original, next);
      node.id = next;
    }
    for (const node of card.querySelectorAll('[aria-controls],[aria-labelledby],[aria-describedby],[for]')) {
      for (const attribute of ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for']) {
        const value = node.getAttribute(attribute);
        if (value) node.setAttribute(attribute, value.split(/\s+/).map(token => identifiers.get(token) || token).join(' '));
      }
    }
    return card;
  }
  const store = createStore(() => window?.localStorage), views = new Map();
  let observer, feedback, feedbackMessage = '';
  const strings = () => window?.RealmTheme?.wishlist || {};
  function announce(message) {
    if (!document || !message) return;
    if (!feedback) {
      feedback = document.createElement('p'); feedback.className = 'realm-wishlist-feedback';
      feedback.setAttribute('role', 'status'); feedback.setAttribute('aria-live', 'polite'); document.body.append(feedback);
    }
    feedback.textContent = message;
  }
  function updateButtons(scope = document) {
    if (!scope) return;
    const saved = new Set(store.state().handles), buttons = Array.from(scope.querySelectorAll('[data-realm-wishlist-toggle]'));
    if (scope.matches?.('[data-realm-wishlist-toggle]')) buttons.push(scope);
    for (const button of buttons) {
      const handle = cleanHandle(button.dataset.realmWishlistToggle);
      if (!handle) { button.hidden = true; continue; }
      const selected = saved.has(handle);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', selected ? button.dataset.wishlistRemoveLabel : button.dataset.wishlistSaveLabel);
      const label = button.querySelector?.('[data-realm-wishlist-text]');
      if (label) {
        const next = selected ? label.dataset.wishlistSavedText : label.dataset.wishlistSaveText;
        if (label.textContent !== next) label.textContent = next;
      }
    }
    for (const count of scope.querySelectorAll('[data-realm-wishlist-count]')) count.textContent = String(saved.size);
  }
  function isCurrent(state, handle, request) {
    return state.root.isConnected && views.get(state.root) === state && state.requests.get(handle) === request && store.state().handles.includes(handle);
  }
  function recovery(state, entry, handle, unavailable) {
    entry.dataset.wishlistState = unavailable ? 'unavailable' : 'error';
    entry.setAttribute('aria-busy', 'false');
    const box = document.createElement('div'); box.className = 'realm-wishlist-recovery';
    const description = document.createElement('p'); description.textContent = unavailable ? state.root.dataset.wishlistUnavailable : state.root.dataset.wishlistError;
    box.append(description);
    if (!unavailable) {
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'realm-button'; retry.dataset.realmWishlistRetry = handle; retry.textContent = state.root.dataset.wishlistRetry; box.append(retry);
    }
    const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'realm-button'; remove.dataset.realmWishlistRemove = handle; remove.textContent = state.root.dataset.wishlistRemove; box.append(remove); entry.replaceChildren(box);
  }
  async function loadEntry(state, entry, handle) {
    state.requests.get(handle)?.controller.abort();
    const request = { controller: new AbortController() }; state.requests.set(handle, request);
    const restoreFocus = entry.contains(document.activeElement);
    entry.dataset.wishlistState = 'loading';
    entry.setAttribute('aria-busy', 'true');
    const loading = document.createElement('div'); loading.className = 'realm-wishlist-recovery';
    const loadingText = document.createElement('p'); loadingText.textContent = state.root.dataset.wishlistLoading;
    const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'realm-button'; remove.dataset.realmWishlistRemove = handle; remove.textContent = state.root.dataset.wishlistRemove;
    loading.append(loadingText, remove); entry.replaceChildren(loading);
    try {
      const result = await fetchCard(handle, state.root.dataset.rootUrl, request.controller.signal, window);
      if (!isCurrent(state, handle, request)) return;
      if (result.unavailable) recovery(state, entry, handle, true);
      else {
        const card = prepareCard(document.importNode(result.card, true));
        entry.dataset.wishlistState = 'ready'; entry.setAttribute('aria-busy', 'false'); entry.replaceChildren(card); updateButtons(card);
        if (typeof window.CustomEvent === 'function') document.dispatchEvent(new window.CustomEvent('realm:product-discovery-updated', { detail: { scope: card } }));
      }
    } catch (_) {
      if (isCurrent(state, handle, request) && !request.controller.signal.aborted) recovery(state, entry, handle, false);
    } finally {
      if (isCurrent(state, handle, request) && restoreFocus && document.activeElement === document.body) entry.querySelector('button,a')?.focus({ preventScroll: true });
      if (state.requests.get(handle) === request) state.requests.delete(handle);
    }
  }
  function reconcileView(state) {
    const handles = store.state().handles, saved = new Set(handles), grid = state.root.querySelector('[data-realm-wishlist-products]');
    if (!grid) return;
    const focused = document.activeElement;
    for (const [handle, entry] of state.entries) if (!saved.has(handle)) {
      state.requests.get(handle)?.controller.abort(); state.requests.delete(handle); entry.remove(); state.entries.delete(handle);
    }
    for (let index = 0; index < handles.length; index++) {
      const handle = handles[index];
      let entry = state.entries.get(handle);
      if (!entry) { entry = document.createElement('li'); entry.dataset.realmWishlistEntry = handle; state.entries.set(handle, entry); grid.append(entry); loadEntry(state, entry, handle); }
      if (grid.children[index] !== entry) grid.insertBefore(entry, grid.children[index] || null);
    }
    if (focused?.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
    const empty = state.root.querySelector('[data-realm-wishlist-empty]'); if (empty) empty.hidden = handles.length > 0;
    const status = state.root.querySelector('[data-realm-wishlist-status]');
    if (status) status.textContent = feedbackMessage || (!store.state().persistent ? state.root.dataset.wishlistStorageWarning : '');
  }
  function mountWithin(scope = document) {
    if (!document) return;
    for (const [root, state] of views) if (!root.isConnected) { state.requests.forEach(request => request.controller.abort()); views.delete(root); }
    const roots = Array.from(scope.querySelectorAll('[data-realm-wishlist-view]'));
    if (scope.matches?.('[data-realm-wishlist-view]')) roots.push(scope);
    for (const root of roots) {
      if (!views.has(root)) views.set(root, { root, entries: new Map(), requests: new Map() });
      reconcileView(views.get(root));
    }
    updateButtons(scope);
  }
  function refresh() {
    updateButtons(); for (const state of views.values()) reconcileView(state);
    if (feedback) feedback.textContent = feedbackMessage || (!store.state().persistent ? strings().storageWarning || '' : '');
  }
  function stateMessage(result) {
    feedbackMessage = result.capacity ? strings().capacity || '' : !result.persistent ? strings().storageWarning || '' : '';
    if (feedbackMessage) announce(feedbackMessage);
    else if (feedback) feedback.textContent = '';
  }
  function focusAfterRemoval(button, handle) {
    const entry = button.closest('[data-realm-wishlist-entry]'), root = button.closest('[data-realm-wishlist-view]');
    if (!entry || !root || store.state().handles.includes(handle)) return;
    const siblings = Array.from(root.querySelectorAll('[data-realm-wishlist-entry]')), index = siblings.indexOf(entry);
    const target = siblings[index + 1] || siblings[index - 1];
    return () => (target?.querySelector('[data-realm-wishlist-toggle],button,a') || root.querySelector('[data-realm-wishlist-browse]'))?.focus({ preventScroll: true });
  }
  function onClick(event) {
    const button = event.target.closest('[data-realm-wishlist-toggle],[data-realm-wishlist-remove],[data-realm-wishlist-retry]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const handle = cleanHandle(button.dataset.realmWishlistToggle || button.dataset.realmWishlistRemove || button.dataset.realmWishlistRetry);
    if (!handle) return;
    if (button.hasAttribute('data-realm-wishlist-retry')) {
      const state = views.get(button.closest('[data-realm-wishlist-view]')), entry = state?.entries.get(handle);
      if (entry) loadEntry(state, entry, handle); return;
    }
    const result = button.hasAttribute('data-realm-wishlist-remove') ? store.remove(handle) : store.toggle(handle);
    const focus = focusAfterRemoval(button, handle); stateMessage(result); refresh(); focus?.();
  }
  if (document && window) {
    document.addEventListener('click', onClick);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountWithin(), { once: true }); else mountWithin();
    document.addEventListener('shopify:section:load', event => mountWithin(event.target));
    window.addEventListener('storage', event => {
      if (event.key !== storageKey && event.key !== null) return;
      let local; try { local = window.localStorage; } catch (_) { return; }
      if (event.storageArea && event.storageArea !== local) return;
      store.synchronize(event.key === null ? null : event.newValue); feedbackMessage = ''; refresh();
    });
    window.addEventListener('pageshow', () => { store.read(); feedbackMessage = ''; mountWithin(); });
    window.addEventListener('pagehide', () => { for (const state of views.values()) { state.requests.forEach(request => request.controller.abort()); state.requests.clear(); for (const [handle, entry] of state.entries) if (entry.dataset.wishlistState === 'loading') { entry.remove(); state.entries.delete(handle); } } });
    if (typeof window.MutationObserver === 'function') {
      observer = new window.MutationObserver(changes => { for (const change of changes) for (const node of change.addedNodes) if (node.nodeType === 1) updateButtons(node); });
      const observe = () => observer.observe(document.body, { childList: true, subtree: true });
      if (document.body) observe(); else document.addEventListener('DOMContentLoaded', observe, { once: true });
    }
  }
  return { cleanHandle, parseHandles, createStore, productUrl, matchingCard, fetchCard, prepareCard, updateButtons, mountWithin, refresh, store, views, onClick, isCurrent };
});
